import {AutoComplete, Button, Checkbox, Drawer, Flex, Input, Modal, Select, Table, TableColumnsType, Tag, Tooltip} from 'antd'
import {SettingOutlined} from '@ant-design/icons'
import {currencyService, providersConfig} from "../components/api";
import ResultModal from "../components/result-modal";
import {useEffect, useState} from "react";
import {ErrorView} from "../components/show-error";
import StringSelector from "../components/string-selector";
import {useSearchParams} from "react-router";
import {
  SettingsBranchesResponse,
  SettingType,
  SettingTypeSchema,
  SettingTable,
  SettingTableSchema,
  FilterType,
  FilterTypeSchema,
  MergeSettingsResponse_Delta,
  Action,
  Result,
} from '../api/ga/config_manager/providersconfig/providers_config_pb'
import {DataEntryParent} from "../components/data-entry-parent";
import CurrencySelector from "../components/currency-selector";
import {AVAILABLE_TAGS} from "../components/tags";
import BranchTagsWidget from "../components/branch-tags-widget";
import {useHasPermission} from "../components/state";
import {Permission} from "../components/permissions";

const UniversalProviderName = "universal"

export default function ProviderSettingsView() {
  const [searchParams, setSearchParams] = useSearchParams()

  const canMerge = useHasPermission(Permission.DevToolsProviderConfigMerge)

  const [modal, contextHolder] = Modal.useModal()

  const [rows, setRows] = useState([] as DataType[])
  const [masterRows, setMasterRows] = useState(new Map<string, DataType>())
  const [branches, setBranches] = useState([] as string[])
  const [branchTags, setBranchTags] = useState({} as Record<string, string[]>)
  const [branch, setBranch] = useState('')
  const [providers, setProviders] = useState([] as string[])
  const [provider, setProvider] = useState('')
  const [envsByProvider, setEnvsByProvider] = useState(new Map<string, string[]>())
  const [universalProviders, setUniversalProviders] = useState(new Set<string>())
  const [env, setEnv] = useState('')
  const [isUniversal, setIsUniversal] = useState(true)

  const [newBranchOpen, setNewBranchOpen] = useState(false)
  const [newBranchName, setNewBranchName] = useState('')

  const [settingsPanelOpen, setSettingsPanelOpen] = useState(false)
  const [branchTagsOpen, setBranchTagsOpen] = useState(true)

  const [tagsOpen, setTagsOpen] = useState(false)
  const [selectedTags, setSelectedTags] = useState([] as string[])

  const [validationOpen, setValidationOpen] = useState(false)
  const [validationRes, setValidationRes] = useState<Result | undefined>(undefined)

  const [exportOpen, setExportOpen] = useState(false)
  const [exportRes, setExportRes] = useState<Result | undefined>(undefined)
  const [exportLoading, setExportLoading] = useState(false)

  const [importOpen, setImportOpen] = useState(false)
  const [importRes, setImportRes] = useState<Result | undefined>(undefined)
  const [importAskOpen, setImportAskOpen] = useState(false)
  const [importFromGitBranch, setImportFromGitBranch] = useState('master')
  const [importLoading, setImportLoading] = useState(false)

  const [mergeOpen, setMergeOpen] = useState(false)
  const [mergeLoading, setMergeLoading] = useState(false)
  const [mergeDeltas, setMergeDeltas] = useState([] as MergeSettingsResponse_Delta[])
  const [mergeParams, setMergeParams] = useState({fromBranch: '', toBranch: ''})

  const [editOpen, setEditOpen] = useState(false)
  const [editRow, setEditRow] = useState<DataType | null>(null)
  const [editRowEdit, setEditRowEdit] = useState<DataType | null>(null)
  // raw textarea text for filter values, so separators can be typed at the end
  const [editValuesText, setEditValuesText] = useState('')

  const [secretWriteOpen, setSecretWriteOpen] = useState(false)
  const [secretWriteValue, setSecretWriteValue] = useState('')
  const [secretCurrentValue, setSecretCurrentValue] = useState('')

  const [newCasinoOpen, setNewCasinoOpen] = useState(false)
  const [newCasinoTableRow, setNewCasinoTableRow] = useState<DataType | null>(null)
  const [newCasinoFrom, setNewCasinoFrom] = useState('')
  const [newCasinoName, setNewCasinoName] = useState('')

  const [newBrandOpen, setNewBrandOpen] = useState(false)
  const [newBrandTableRow, setNewBrandTableRow] = useState<DataType | null>(null)
  const [newBrandFrom, setNewBrandFrom] = useState('')
  const [newBrandName, setNewBrandName] = useState('')

  const [newFilterOpen, setNewFilterOpen] = useState(false)
  const [newFilterTableRow, setNewFilterTableRow] = useState<DataType | null>(null)
  const [newFilterType, setNewFilterType] = useState<FilterType>(FilterType.FILTER_UNKNOWN)
  const [newFilterSetting, setNewFilterSetting] = useState('')

  const [modelTree, setModelTree] = useState([] as ModelType[])
  const [modelMap, setModelMap] = useState(new Map<string, ModelInfo>())

  const [selectedEntityKeys, setSelectedEntityKeys] = useState([] as string[])
  const [selectedEntityTable, setSelectedEntityTable] = useState<SettingTable | null>(null)
  const [selectedEntityRows, setSelectedEntityRows] = useState([] as DataType[])

  const [groupEditOpen, setGroupEditOpen] = useState(false)
  const [groupEditValues, setGroupEditValues] = useState({} as Record<string, DataType>)
  const [groupEditModelSettings, setGroupEditModelSettings] = useState([] as ModelType[])
  const [groupEditUniqueByText, setGroupEditUniqueByText] = useState(new Map<string, Map<string, DataType>>())

  const [tableSearch, setTableSearch] = useState('')

  const [currencies, setCurrencies] = useState([] as string[])

  interface DataType {
    key: string
    branch: string
    env: string
    provider: string
    table: SettingTable
    brand: string
    casino: string
    filterType: FilterType
    setting: string
    settingType: SettingType
    value: string
    vaultPath: string
    vaultKey: string
    values: string[]
    action: Action
    virtual: boolean
    is_env_layer: boolean
    is_table_layer: boolean
    is_entity_layer: boolean
    children?: DataType[]
  }

  interface ModelInfo {
    settingType: SettingType
    comment: string
    required: boolean
  }

  interface ModelType {
    key: string
    provider: string
    table: SettingTable
    setting: string
    is_provider_layer: boolean
    is_table_layer: boolean
    children?: ModelType[]
  }

  const loadBranches = (initProvider?: string, initEnv?: string, initBranch?: string) => {
    providersConfig.settingsBranches({})
      .then((resp: SettingsBranchesResponse) => {
        const branchesMap = resp.branches ?? {}
        setBranches(Object.keys(branchesMap).sort())
        setBranchTags(Object.fromEntries(
          Object.entries(branchesMap).map(([name, info]) => [name, info.tags ?? []])
        ))

        const provs = resp.providers ?? {}
        setProviders(Object.keys(provs).sort())

        const newEnvsByProvider = new Map<string, string[]>()
        const newUniversalProviders = new Set<string>()
        for (const [p, envs] of Object.entries(provs)) {
          if (envs?.isUniversal) newUniversalProviders.add(p)
          newEnvsByProvider.set(p, ['', ...(envs?.envs ?? [])].sort())
        }
        setEnvsByProvider(newEnvsByProvider)
        setUniversalProviders(newUniversalProviders)

        if (initProvider) setProvider(initProvider)
        if (initEnv) setEnv(initEnv)
        if (initBranch) setBranch(initBranch)
      })
      .catch((e) => {
        ErrorView(modal, 'settingsBranches error', e)
      })
  }

  const tableLabel = (t: SettingTable) =>
    SettingTableSchema.values.find((v) => v.number === t)?.name.replace(/^TABLE_/, '').toLowerCase() ?? String(t)

  const filterTypeLabel = (t: FilterType) =>
    FilterTypeSchema.values.find((v) => v.number === t)?.name.replace(/^FILTER_/, '').toLowerCase() ?? String(t)

  const entityKey = (r: DataType) => {
    if (r.table === SettingTable.TABLE_BRAND) return r.brand
    if (r.table === SettingTable.TABLE_CASINO) return r.casino
    if (r.table === SettingTable.TABLE_FILTER) return filterTypeLabel(r.filterType)
    return ''
  }

const vaultPathOptions = (r: DataType): {value: string}[] => {
    const envPrefixes = (r.env === 'dev' || r.env === 'demo') ? ['dev-shared', 'int'] : [r.env]
    const collectBrands = (items: DataType[]): string[] =>
      items.flatMap(item => [
        ...(item.table === SettingTable.TABLE_BRAND && item.brand ? [item.brand] : []),
        ...(item.children ? collectBrands(item.children) : []),
      ])
    const brands = Array.from(new Set(collectBrands(rows)))
    return envPrefixes.flatMap(env =>
      r.table === SettingTable.TABLE_BRAND
        ? (brands.length ? brands : [r.brand]).map(brand => ({value: `${env}/providers/${r.provider}/brands/${brand}`}))
        : r.table === SettingTable.TABLE_CASINO
          ? [{value: `${env}/providers/${r.provider}/casinos/${r.casino}`}]
          : [{value: `${env}/providers/${r.provider}`}]
    )
  }

  const findLeaf = (entity: DataType, settingPath: string): DataType | undefined => {
    if (!entity.children) return undefined
    for (const child of entity.children) {
      if (child.setting === settingPath) return child
      if (settingPath.startsWith(child.setting + '.')) return findLeaf(child, settingPath)
    }
    return undefined
  }

  const modelSettingType = (m: ModelType): SettingType =>
    modelMap.get(`${m.provider}:${m.table}:${m.setting}`)?.settingType ?? SettingType.UNKNOWN

  const leafValueText = (leaf?: DataType | null): string => {
    if (!leaf || leaf.virtual) return ''
    if (leaf.vaultPath || leaf.vaultKey) return `${leaf.vaultPath} . ${leaf.vaultKey}`
    if (leaf.table === SettingTable.TABLE_FILTER) return (leaf.values ?? []).join(',')
    return leaf.value
  }

  const multiSettingFilterTypes = new Set<FilterType>([
    FilterType.FILTER_CURRENCIES_ALLOWED_BRAND,
    FilterType.FILTER_CURRENCIES_ALLOWED_PRODUCER,
    FilterType.FILTER_TERRITORIES_RESTRICTED_BRAND,
    FilterType.FILTER_TERRITORIES_RESTRICTED_PRODUCER,
  ])

  const currenciesFilterTypes = new Set<FilterType>([
    FilterType.FILTER_CURRENCIES_ALLOWED_DEFAULT,
    FilterType.FILTER_CURRENCIES_ALLOWED_BRAND,
    FilterType.FILTER_CURRENCIES_ALLOWED_PRODUCER,
  ])

  const loadSettings = () => {
    if (!branch || !provider) return
    providersConfig.settings({
      env: env,
      branch: branch,
      // modelBranch: 'master',
      provider: provider,
    }).then((resp) => {
        setIsUniversal(true)

        const modelsByProvTable = new Map<string, Map<string, SettingType>>()
        const newModelMap = new Map<string, ModelInfo>()
        for (const m of resp.models ?? []) {
          if (m.provider !== UniversalProviderName) setIsUniversal(false)
          const k = `${m.provider}:${m.table}`
          if (!modelsByProvTable.has(k)) modelsByProvTable.set(k, new Map())
          modelsByProvTable.get(k)!.set(m.setting, m.settingType)
          newModelMap.set(`${m.provider}:${m.table}:${m.setting}`, {
            settingType: m.settingType,
            comment: m.comment,
            required: m.required,
          })
        }
        setModelMap(newModelMap)

        const settingTypeOf = (prov: string, tbl: SettingTable, sett: string): SettingType | undefined =>
          (newModelMap.get(`${prov}:${tbl}:${sett}`) ?? newModelMap.get(`${UniversalProviderName}:${tbl}:${sett}`))
            ?.settingType

        // Build model tree: provider -> table -> setting (leaf).
        const modelProvMap = new Map<string, Map<SettingTable, ModelType[]>>()
        for (const m of resp.models ?? []) {
          if (!modelProvMap.has(m.provider)) modelProvMap.set(m.provider, new Map())
          const tableMap = modelProvMap.get(m.provider)!
          if (!tableMap.has(m.table)) tableMap.set(m.table, [])
          tableMap.get(m.table)!.push({
            key: `${m.provider}:${m.table}:${m.setting}`,
            provider: m.provider,
            table: m.table,
            setting: m.setting,
            is_provider_layer: false,
            is_table_layer: false,
          })
        }
        const newModelTree: ModelType[] = []
        modelProvMap.forEach((tableMap, prov) => {
          const tables: ModelType[] = []
          tableMap.forEach((settings, tbl) => {
            settings.sort((a, b) => a.setting.localeCompare(b.setting))
            tables.push({
              key: `${prov}:${tbl}`,
              provider: prov,
              table: tbl,
              setting: '',
              is_provider_layer: false,
              is_table_layer: true,
              children: settings,
            })
          })
          tables.sort((a, b) => a.table - b.table)
          newModelTree.push({
            key: prov,
            provider: prov,
            table: 0 as SettingTable,
            setting: '',
            is_provider_layer: true,
            is_table_layer: false,
            children: tables,
          })
        })
        newModelTree.sort((a, b) => a.provider.localeCompare(b.provider))
        setModelTree(newModelTree)

        type Row = {
          branch: string
          env: string
          provider: string
          table: SettingTable
          brand: string
          casino: string
          filterType: FilterType
          setting: string
          settingType: SettingType
          value: string
          vaultPath: string
          vaultKey: string
          values: string[]
          action: Action
          virtual: boolean
        }
        const flat: Row[] = []
        for (const r of resp.providers ?? []) {
          flat.push({
            branch: r.branch, env: r.env, provider: r.provider,
            table: SettingTable.TABLE_PROVIDER, brand: '', casino: '', filterType: FilterType.FILTER_UNKNOWN,
            setting: r.setting,
            settingType: settingTypeOf(r.provider, SettingTable.TABLE_PROVIDER, r.setting) ?? r.settingType,
            value: r.value, vaultPath: r.vaultPath, vaultKey: r.vaultKey, values: [], action: r.action,
            virtual: false,
          })
        }
        for (const r of resp.brands ?? []) {
          flat.push({
            branch: r.branch, env: r.env, provider: r.provider,
            table: SettingTable.TABLE_BRAND, brand: r.brand, casino: '', filterType: FilterType.FILTER_UNKNOWN,
            setting: r.setting,
            settingType: settingTypeOf(r.provider, SettingTable.TABLE_BRAND, r.setting) ?? r.settingType,
            value: r.value, vaultPath: r.vaultPath, vaultKey: r.vaultKey, values: [], action: r.action,
            virtual: false,
          })
        }
        for (const r of resp.casinos ?? []) {
          flat.push({
            branch: r.branch, env: r.env, provider: r.provider,
            table: SettingTable.TABLE_CASINO, brand: '', casino: r.casino, filterType: FilterType.FILTER_UNKNOWN,
            setting: r.setting,
            settingType: settingTypeOf(r.provider, SettingTable.TABLE_CASINO, r.setting) ?? r.settingType,
            value: r.value, vaultPath: r.vaultPath, vaultKey: r.vaultKey, values: [], action: r.action,
            virtual: false,
          })
        }
        for (const r of resp.filters ?? []) {
          flat.push({
            branch: r.branch, env: r.env, provider: r.provider,
            table: SettingTable.TABLE_FILTER, brand: '', casino: '', filterType: r.type,
            setting: r.setting,
            settingType: SettingType.ARRAY,
            value: '', vaultPath: '', vaultKey: '', values: r.values ?? [], action: r.action,
            virtual: false,
          })
        }

        // Extend with virtual rows from model schema: for each existing
        // (env, provider, table, entity) tuple, add a virtual row for every model
        // setting that is not already present in the real data.
        {
          const entKey = (env: string, prov: string, tbl: SettingTable) => `${env}:${prov}:${tbl}`
          const rowKey = (env: string, prov: string, tbl: SettingTable, ent: string, sett: string) =>
            `${env}:${prov}:${tbl}:${ent}:${sett}`
          const entityOf = (r: Row): string =>
            r.table === SettingTable.TABLE_BRAND ? r.brand
              : r.table === SettingTable.TABLE_CASINO ? r.casino
                : r.table === SettingTable.TABLE_FILTER ? String(r.filterType)
                  : ''

          const realKeys = new Set<string>()
          const entitiesByEnvProvTable = new Map<string, Set<string>>()
          for (const r of flat) {
            const ent = entityOf(r)
            realKeys.add(rowKey(r.env, r.provider, r.table, ent, r.setting))
            const ek = entKey(r.env, r.provider, r.table)
            if (!entitiesByEnvProvTable.has(ek)) entitiesByEnvProvTable.set(ek, new Set())
            entitiesByEnvProvTable.get(ek)!.add(ent)
          }

          entitiesByEnvProvTable.forEach((entities, ek) => {
            const [envStr, prov, tblStr] = ek.split(':')
            const tbl = Number(tblStr) as SettingTable
            const models = modelsByProvTable.get(`${prov}:${tbl}`) ?? modelsByProvTable.get(`${UniversalProviderName}:${tbl}`)
            entities.forEach((ent) => {
              models?.forEach((settingType, setting) => {
                if (realKeys.has(rowKey(envStr, prov, tbl, ent, setting))) return
                flat.push({
                  branch: 'master', env: envStr, provider: prov,
                  table: tbl,
                  brand: tbl === SettingTable.TABLE_BRAND ? ent : '',
                  casino: tbl === SettingTable.TABLE_CASINO ? ent : '',
                  filterType: tbl === SettingTable.TABLE_FILTER ? Number(ent) as FilterType : FilterType.FILTER_UNKNOWN,
                  setting: setting,
                  settingType: settingType,
                  value: '', vaultPath: '', vaultKey: '', values: [],
                  action: Action.NO_OR_UPSET,
                  virtual: true,
                })
              })
            })
          })
        }

        const treeMap = new Map<string, Map<SettingTable, Map<string, Map<string, DataType>>>>()
        const masterRowsMap = new Map<string, DataType>()

        const ensure = (r: Row, sett: string): DataType => {
          let tableMap = treeMap.get(r.env)
          if (!tableMap) {
            tableMap = new Map()
            treeMap.set(r.env, tableMap)
          }
          let entMap = tableMap.get(r.table)
          if (!entMap) {
            entMap = new Map()
            tableMap.set(r.table, entMap)
          }
          const ent = r.table === SettingTable.TABLE_BRAND ? r.brand
            : r.table === SettingTable.TABLE_CASINO ? r.casino
              : r.table === SettingTable.TABLE_FILTER ? String(r.filterType)
                : ''
          let settMap = entMap.get(ent)
          if (!settMap) {
            settMap = new Map()
            entMap.set(ent, settMap)
          }
          let node = settMap.get(sett)
          if (!node) {
            node = {
              key: `${r.env}:${r.table}:${ent}:${sett}`,
              branch: r.branch, env: r.env, provider: r.provider,
              table: r.table, brand: r.brand, casino: r.casino, filterType: r.filterType,
              setting: sett,
              settingType: SettingType.UNKNOWN,
              value: '', vaultPath: '', vaultKey: '', values: [],
              action: Action.NO_OR_UPSET,
              virtual: true,
              is_env_layer: false, is_table_layer: false, is_entity_layer: false,
              children: undefined,
            }
            settMap.set(sett, node)
            const lastDot = sett.lastIndexOf('.')
            if (lastDot !== -1) {
              const parent = ensure(r, sett.substring(0, lastDot))!
              parent.children = [...(parent.children ?? []), node]
            }
          }
          return node
        }

        for (const r of flat) {
          const node = ensure(r, r.setting)!
          if (!r.virtual) {
            node.branch = r.branch
            node.settingType = r.settingType
            node.value = r.value
            node.vaultPath = r.vaultPath
            node.vaultKey = r.vaultKey
            node.values = r.values
            node.action = r.action
            node.virtual = false
            if (r.branch === 'master') {
              masterRowsMap.set(node.key, node)
            }
          } else if (node.settingType === SettingType.UNKNOWN) {
            node.settingType = r.settingType
          }
        }

        const sortBySetting = (a: DataType, b: DataType) => a.setting.localeCompare(b.setting)
        const tableHasEntity = (t: SettingTable) =>
          t === SettingTable.TABLE_BRAND || t === SettingTable.TABLE_CASINO || t === SettingTable.TABLE_FILTER

        const roots: DataType[] = []
        treeMap.forEach((tableMap, envKey) => {
          const tableLayers: DataType[] = []
          let envBranch = 'master'
          tableMap.forEach((entMap, tbl) => {
            const entityLayers: DataType[] = []
            let tableBranch = 'master'
            entMap.forEach((settMap, entStr) => {
              const settingRoots: DataType[] = []
              let entityBranch = 'master'
              let sampleNode: DataType | null = null
              settMap.forEach((node) => {
                if (node.children) node.children.sort(sortBySetting)
                if (node.setting.lastIndexOf('.') === -1) settingRoots.push(node)
                if (node.branch !== 'master') entityBranch = node.branch
                sampleNode = node
              })
              settingRoots.sort(sortBySetting)
              if (entityBranch !== 'master') tableBranch = entityBranch

              if (tableHasEntity(tbl) && sampleNode) {
                const sample = sampleNode as DataType
                entityLayers.push({
                  key: `${envKey}:${tbl}:${entStr}`,
                  branch: entityBranch, env: envKey, provider: provider,
                  table: tbl,
                  brand: tbl === SettingTable.TABLE_BRAND ? entStr : '',
                  casino: tbl === SettingTable.TABLE_CASINO ? entStr : '',
                  filterType: tbl === SettingTable.TABLE_FILTER ? sample.filterType : FilterType.FILTER_UNKNOWN,
                  setting: '',
                  settingType: SettingType.UNKNOWN,
                  value: '', vaultPath: '', vaultKey: '', values: [],
                  action: Action.NO_OR_UPSET,
                  virtual: false,
                  is_env_layer: false, is_table_layer: false, is_entity_layer: true,
                  children: settingRoots,
                })
              } else {
                entityLayers.push(...settingRoots)
              }
            })
            if (tableBranch !== 'master') envBranch = tableBranch

            if (tableHasEntity(tbl)) {
              entityLayers.sort((a, b) => {
                const sa: string = a.brand || a.casino || filterTypeLabel(a.filterType)
                const sb: string = b.brand || b.casino || filterTypeLabel(b.filterType)
                return sa.localeCompare(sb)
              })
            } else {
              entityLayers.sort(sortBySetting)
            }
            tableLayers.push({
              key: `${envKey}:${tbl}`,
              branch: tableBranch, env: envKey, provider: provider,
              table: tbl, brand: '', casino: '', filterType: FilterType.FILTER_UNKNOWN,
              setting: '',
              settingType: SettingType.UNKNOWN,
              value: String(entityLayers.length), vaultPath: '', vaultKey: '', values: [],
              action: Action.NO_OR_UPSET,
              virtual: false,
              is_env_layer: false, is_table_layer: true, is_entity_layer: false,
              children: entityLayers,
            })
          })
          tableLayers.sort((a, b) => a.table - b.table)
          roots.push({
            key: envKey,
            branch: envBranch, env: envKey, provider: provider,
            table: 0 as SettingTable, brand: '', casino: '', filterType: FilterType.FILTER_UNKNOWN,
            setting: '',
            settingType: SettingType.UNKNOWN,
            value: '', vaultPath: '', vaultKey: '', values: [],
            action: Action.NO_OR_UPSET,
            virtual: false,
            is_env_layer: true, is_table_layer: false, is_entity_layer: false,
            children: tableLayers,
          })
        })
        roots.sort((a, b) => a.env.localeCompare(b.env))

        while (roots.length === 1 && roots[0].children?.length) {
          const children = roots[0].children
          roots.length = 0
          roots.push(...children)
        }

        setRows(roots)
        setMasterRows(masterRowsMap)
      })
      .catch((e) => {
        ErrorView(modal, 'settings error', e)
      })
  }

  useEffect(() => {
    const initProvider = searchParams.get('provider') ?? ''
    setProvider(initProvider)
    const initEnv = searchParams.get('env') ?? ''
    setEnv(initEnv)
    const initBranch = searchParams.get('branch') ?? 'master'
    setBranch(initBranch)
    setTableSearch(searchParams.get('search') ?? '')
    loadBranches(initProvider, initEnv, initBranch)

    currencyService.list({})
      .then((resp) => {
        setCurrencies(Array.from(resp.currencies ?? []).sort())
      })
      .catch((e) => {
        ErrorView(modal, 'currency list error', e)
      })
  }, [])

  // Sync branch from the URL so external navigation (e.g. the branch tags widget) updates the page.
  useEffect(() => {
    const b = searchParams.get('branch') ?? ''
    if (b && b !== branch) setBranch(b)
  }, [searchParams])

  useEffect(() => {
    setSearchParams((par) => {
      if (tableSearch) par.set('search', tableSearch); else par.delete('search')
      return par
    })
  }, [tableSearch])

  useEffect(() => {
    setSearchParams((par) => {
      if (provider) par.set('provider', provider); else par.delete('provider')
      return par
    })

    if (!provider) return

    const availEnvs = envsByProvider.get(provider) ?? []
    if (!availEnvs.includes(env)) {
      setEnv('')
    }
  }, [provider])

  useEffect(() => {
    setSearchParams((par) => {
      par.set('env', env)
      return par
    })
  }, [env])

  useEffect(() => {
    if (!branch) return

    setSearchParams((par) => {
      par.set('branch', branch)
      return par
    })
  }, [branch])

  useEffect(() => {
    // console.log(provider, "_", env, "_", branch)
    setRows([])
    setMasterRows(new Map<string, DataType>())
    loadSettings()
  }, [provider, env, branch])

  const displayValue = (r: DataType): string => {
    if (r.is_table_layer && r.table === SettingTable.TABLE_CASINO) return 'casinos: ' + r.value
    if (r.is_table_layer && r.table === SettingTable.TABLE_BRAND) return 'brands: ' + r.value
    if (r.is_entity_layer && r.table === SettingTable.TABLE_CASINO && r.children?.length) return r.children.map(displayValue).join(' ')
    if (r.children?.length) return ''
    if (r.virtual) return '<default>'
    if (r.vaultPath || r.vaultKey) return `${r.vaultPath} . ${r.vaultKey}`
    if (r.table === SettingTable.TABLE_FILTER) return (r.values ?? []).join(', ')
    return r.value
  }

  const upsertSettingPayload = (env: string, branch: string, row: DataType, override: Partial<DataType>, existing?: any) => {
    const r = {...row, ...override}
    const payload: any = existing ?? {providers: [], brands: [], casinos: [], filters: []}
    if (r.table === SettingTable.TABLE_PROVIDER) {
      payload.providers.push({
        env: env,
        branch: branch,
        provider: r.provider,
        setting: r.setting,
        settingType: r.settingType,
        value: r.value,
        vaultPath: r.vaultPath,
        vaultKey: r.vaultKey,
        action: r.action,
      })
    } else if (r.table === SettingTable.TABLE_BRAND) {
      payload.brands.push({
        env: env,
        branch: branch,
        provider: r.provider,
        brand: r.brand,
        setting: r.setting,
        settingType: r.settingType,
        value: r.value,
        vaultPath: r.vaultPath,
        vaultKey: r.vaultKey,
        action: r.action,
      })
    } else if (r.table === SettingTable.TABLE_CASINO) {
      payload.casinos.push({
        env: env,
        branch: branch,
        provider: r.provider,
        casino: r.casino,
        setting: r.setting,
        settingType: r.settingType,
        value: r.value,
        vaultPath: r.vaultPath,
        vaultKey: r.vaultKey,
        action: r.action,
      })
    } else if (r.table === SettingTable.TABLE_FILTER) {
      payload.filters.push({
        env: env,
        branch: branch,
        provider: r.provider,
        type: r.filterType,
        setting: r.setting,
        values: r.values,
        action: r.action,
      })
    }
    return payload
  }

  const deleteSettingPayload = (env: string, branch: string, row: DataType, existing?: any) => {
    const payload: any = existing ?? {providers: [], brands: [], casinos: [], filters: []}
    if (row.table === SettingTable.TABLE_PROVIDER) {
      payload.providers.push({
        env: env,
        branch: branch,
        provider: row.provider,
        setting: row.setting,
      })
    } else if (row.table === SettingTable.TABLE_BRAND) {
      payload.brands.push({
        env: env,
        branch: branch,
        provider: row.provider,
        brand: row.brand,
        setting: row.setting,
      })
    } else if (row.table === SettingTable.TABLE_CASINO) {
      payload.casinos.push({
        env: env,
        branch: branch,
        provider: row.provider,
        casino: row.casino,
        setting: row.setting,
      })
    } else if (row.table === SettingTable.TABLE_FILTER) {
      payload.filters.push({
        env: env,
        branch: branch,
        provider: row.provider,
        type: row.filterType,
        setting: row.setting,
      })
    }
    return payload
  }

  const columns: TableColumnsType<DataType> = [
    {
      title: 'env / table / entity / setting',
      dataIndex: 'setting',
      key: 'setting',
      width: '35vw',
      render: (_, row) => {
        if (row.is_env_layer) return row.env
        if (row.is_table_layer) return tableLabel(row.table)
        if (row.is_entity_layer) return entityKey(row)
        const lastDot = row.setting.lastIndexOf('.')
        const label = lastDot !== -1 ? row.setting.substring(lastDot + 1) : row.setting
        const model = modelMap.get(`${row.provider}:${row.table}:${row.setting}`)
        const text = model?.required ? <span style={{fontWeight: 'bold'}}>{label}</span> : label
        return model?.comment ? <Tooltip title={model.comment}>{text}</Tooltip> : text
      },
    },
    {
      title: 'branch',
      dataIndex: 'branch',
      key: 'branch',
      // render: (_, row) => row.brand,
    },
    {
      title: 'value',
      dataIndex: 'value',
      key: 'value',
      width: '35vw',
      render: (_, row) => displayValue(row),
    },
    {
      title: 'type',
      dataIndex: 'settingType',
      key: 'settingType',
      width: '8vw',
      render: (_, row) => row.children?.length
        ? (row.is_env_layer || row.is_table_layer || row.is_entity_layer ? '' : '')
        : SettingTypeSchema.values.find((v) => v.number === row.settingType)?.name.toLowerCase() ?? row.settingType,
    },
    {
      title: 'actions',
      key: 'actions',
      width: '12vw',
      render: (_, row) => {
        if (branch === 'master') return ''
        if (row.is_table_layer && row.table === SettingTable.TABLE_CASINO) {
          return (
            <Button size="small" onClick={() => {
              setNewCasinoTableRow(row)
              setNewCasinoFrom('')
              setNewCasinoName('')
              setNewCasinoOpen(true)
            }}>new</Button>
          )
        }
        if (row.is_table_layer && row.table === SettingTable.TABLE_BRAND) {
          return (
            <Button size="small" onClick={() => {
              setNewBrandTableRow(row)
              setNewBrandFrom('')
              setNewBrandName('')
              setNewBrandOpen(true)
            }}>new</Button>
          )
        }
        if (row.is_table_layer && row.table === SettingTable.TABLE_FILTER) {
          return (
            <Button size="small" onClick={() => {
              setNewFilterTableRow(row)
              setNewFilterType(FilterType.FILTER_UNKNOWN)
              setNewFilterSetting('')
              setNewFilterOpen(true)
            }}>new</Button>
          )
        }
        if (row.is_env_layer || row.is_table_layer || row.is_entity_layer) return ''
        if (row.children?.length) return ''
        return (
          <Flex gap={4}>
            <Button size="small" onClick={() => {
              setEditRow(row)
              setEditRowEdit({...row, values: [...(row.values ?? [])]})
              setEditValuesText((row.values ?? []).join(', '))
              setEditOpen(true)
            }}>edit</Button>
            {!row.virtual && (
              <Button size="small" danger onClick={() => {
                modal.confirm({
                  title: 'Delete setting',
                  content: `Delete "${row.setting}" (${row.provider}/${tableLabel(row.table)}${entityKey(row) ? '/' + entityKey(row) : ''})?`,
                  onOk: () => {
                    if (row.branch === branch) {
                      providersConfig.deleteSettings(deleteSettingPayload(row.env, branch, row))
                        .then(() => loadSettings())
                        .catch((e: any) => { ErrorView(modal, 'deleteSettings error', e) })
                    } else {
                      providersConfig.upsertSettings(upsertSettingPayload(row.env, branch, row, {action: Action.DELETE}))
                        .then(() => loadSettings())
                        .catch((e: any) => { ErrorView(modal, 'upsertSettings error', e) })
                    }
                  },
                })
              }}>{row.branch === 'master' ? 'remove from master' : 'reset'}</Button>
            )}
          </Flex>
        )
      },
    },
  ]

  return (
    <>
      <div style={{maxWidth: 1400, margin: '0 auto', padding: '24px 24px 40px', textAlign: 'left'}}>
      <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 0}}>
        <h1 style={{fontSize: 32, fontWeight: 900, margin: 0, display: 'flex', alignItems: 'baseline', gap: 24}}>
          <span>Provider Settings</span>
          {/*{provider && <span style={{fontSize: 16, fontWeight: 700}}>*/}
          {/*  for*/}
          {/*  {isUniversal && <span style={{fontSize: 16, fontWeight: 700}}>universal</span>}*/}
          {/*  <span style={{fontSize: 32, fontWeight: 700}}>{provider}</span> provider*/}
          {/*</span>}*/}
        </h1>
        {/* Settings panel toggle — opens a right-side drawer holding auxiliary controls. */}
        <Tooltip title="Settings">
          <Button
            icon={<SettingOutlined />}
            onClick={() => setSettingsPanelOpen(true)}
          />
        </Tooltip>
      </div>
      <Drawer
        title="Options"
        placement="right"
        open={settingsPanelOpen}
        onClose={() => setSettingsPanelOpen(false)}
      >
        <DataEntryParent text="Branch tags">
          <Button onClick={() => setBranchTagsOpen(true)}>Branch tags</Button>
        </DataEntryParent>
        <DataEntryParent text="Validate">
          <Button onClick={() => {
            providersConfig.validateSettingsBranch({env: env, branch: branch})
              .then((resp) => {
                setValidationRes(resp.res)
                setValidationOpen(true)
              })
              .catch((e: any) => { ErrorView(modal, 'validateSettingsBranch error', e) })
          }}>Validate Branch</Button>
        </DataEntryParent>
        {branch && branch !== 'master' && (
          <>
            <DataEntryParent text="Delete">
              <Button danger onClick={() => {
                modal.confirm({
                  title: 'Delete branch',
                  content: `Delete branch "${branch}" on "${env}" and all its settings?`,
                  onOk: () => {
                    providersConfig.deleteSettings({
                      providers: [{env: env, branch: branch} as any],
                      brands: [{env: env, branch: branch} as any],
                      casinos: [{env: env, branch: branch} as any],
                      filters: [{env: env, branch: branch} as any],
                    })
                      .then(() => {
                        setBranch('master');
                        loadBranches();
                      })
                      .catch((e: any) => { ErrorView(modal, 'deleteSettings error', e) })
                  },
                })
              }}>Delete branch</Button>
            </DataEntryParent>
            <DataEntryParent text="Import">
              <Button danger onClick={() => {
                setImportFromGitBranch('master');
                setImportAskOpen(true)
              }}>Import from git</Button>
            </DataEntryParent>
          </>
        )}
      </Drawer>
      {canMerge && (
        <BranchTagsWidget kind="provider" open={branchTagsOpen} onClose={() => setBranchTagsOpen(false)} />
      )}
      {/* Controls grid — branch level actions */}
      <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: 16, marginBottom: 0, alignItems: 'start'}}>
        <DataEntryParent text="branch">
          <StringSelector
            items={branches}
            value={[branch]}
            onChange={(value: string[]) => setBranch(value ? value[0] : '')}
          />
        </DataEntryParent>

        <DataEntryParent text="New">
          <Button onClick={() => {
            setNewBranchName('');
            setNewBranchOpen(true);
          }}>New Branch from current</Button>
        </DataEntryParent>
        {branch && branch !== 'master' && (
          <>
            <DataEntryParent text="Merge">
              <Button danger loading={mergeLoading} onClick={() => {
                const params = {fromBranch: branch, toBranch: 'master'}
                providersConfig.mergeSettingsCompare(params)
                  .then((resp) => {
                    setMergeParams(params);
                    setMergeDeltas(resp.deltas ?? []);
                    setMergeOpen(true)
                  })
                  .catch((e: any) => {
                    if (String(e?.message ?? e).includes('no data for merge')) {
                      modal.warning({title: 'Merge', content: 'No data for merge'})
                    } else {
                      ErrorView(modal, 'mergeModelCompare error', e)
                    }
                  })
              }}>Merge branch</Button>
            </DataEntryParent>
            <DataEntryParent text="Tags">
              <Flex gap={8} align="center" wrap>
                <Button onClick={() => {
                  setSelectedTags(branchTags[branch] ?? [])
                  setTagsOpen(true)
                }}>Edit tags</Button>
                <Flex gap={4} wrap>
                  {(branchTags[branch] ?? []).map((t) => <Tag key={t}>{t}</Tag>)}
                </Flex>
              </Flex>
            </DataEntryParent>
          </>
        )}
      </div>
      {/* Controls grid — filters and export */}
      <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: 16, marginBottom: 16, alignItems: 'start'}}>
        <DataEntryParent text="provider">
          <StringSelector
            items={providers}
            value={[provider]}
            optionLabel={(p) => universalProviders.has(p)
              ? <span>{p} <span style={{opacity: 0.5}}>universal</span></span>
              : p}
            onChange={(value: string[]) => setProvider(value ? value[0] : '')}
          />
        </DataEntryParent>

        <DataEntryParent text="env">
          <StringSelector
            items={provider
              ? (envsByProvider.get(provider) ?? [])
              : Array.from(new Set(Array.from(envsByProvider.values()).flat())).sort()}
            value={[env]}
            onChange={(value: string[]) => setEnv(value ? value[0] : '')}
          />
        </DataEntryParent>

        <DataEntryParent text="search">
          <Input.Search
            value={tableSearch}
            placeholder="search"
            allowClear
            onChange={(e) => setTableSearch(e.target.value)}
          />
        </DataEntryParent>

        <DataEntryParent text="Export">
          <Button loading={exportLoading} onClick={() => {
            setExportLoading(true);
            providersConfig.exportSettings({fromBranch: branch})
              .then((resp) => {
                setExportRes(resp.res)
                setExportOpen(true)
                // if (resp.gitMrLink) window.open(resp.gitMrLink, '_blank')
                if (resp.gitBranch || resp.gitMrLink) {
                  modal.info({
                    title: 'Export pushed to git',
                    content: (
                      <p>
                        Branch <b>{resp.gitBranch}</b> created.{' '}
                        {resp.gitMrLink && <a href={resp.gitMrLink} target="_blank" rel="noreferrer">Open merge request</a>}
                      </p>
                    ),
                  })
                }
              })
              .catch((e: any) => { ErrorView(modal, 'exportSettingsDo error', e) })
              .finally(() => setExportLoading(false))
          }}>Export to git</Button>
        </DataEntryParent>
        {selectedEntityKeys.length > 0 && branch !== 'master' && (
          <DataEntryParent text="Group">
            <Button onClick={() => {
              const keysSet = new Set(selectedEntityKeys)
              const collectByKeys = (nodes: DataType[], acc: DataType[]): DataType[] => {
                for (const n of nodes) {
                  if (keysSet.has(n.key)) acc.push(n)
                  if (n.children) collectByKeys(n.children, acc)
                }
                return acc
              }
              const selectedRows = collectByKeys(rows, [])
              setSelectedEntityRows(selectedRows)

              const first = selectedRows[0]
              const provNode = modelTree.find((p) => p.provider === first?.provider)
              const tableNode = provNode?.children?.find((c) => c.table === first?.table)
              const modelSettings = tableNode?.children ?? []

              const uniqueByTextMap = new Map<string, Map<string, DataType>>()
              const initial: Record<string, DataType> = {}
              for (const m of modelSettings) {
                const uniqueByText = new Map<string, DataType>()
                let hasMissing = false
                for (const e of selectedRows) {
                  const leaf = findLeaf(e, m.setting)
                  if (!leaf || leaf.virtual) {
                    hasMissing = true
                    continue
                  }
                  const text = leafValueText(leaf)
                  if (!uniqueByText.has(text)) uniqueByText.set(text, leaf)
                }
                uniqueByTextMap.set(m.setting, uniqueByText)
                if (!hasMissing && uniqueByText.size === 1) {
                  initial[m.setting] = uniqueByText.values().next().value!
                }
              }

              setGroupEditModelSettings(modelSettings)
              setGroupEditUniqueByText(uniqueByTextMap)
              setGroupEditValues(initial)
              setGroupEditOpen(true)
            }}>Group Edit ({selectedEntityKeys.length})</Button>
          </DataEntryParent>
        )}
      </div>
      <div style={{background: 'var(--surface-white)', borderRadius: 'var(--radius-basic)', border: '1px solid var(--border)', overflow: 'hidden'}}>
      <Table
        dataSource={(() => {
          const base = rows.filter((r) => !provider || r.provider === provider)
          if (!tableSearch) return base

          const needle = tableSearch.toLowerCase()
          const matchSelf = (n: DataType) => {
            const {children, ...own} = n
            return JSON.stringify(own).toLowerCase().includes(needle)
          }
          const filterTree = (nodes: DataType[]): DataType[] => {
            const out: DataType[] = []
            for (const n of nodes) {
              if (matchSelf(n)) {
                out.push(n)
              } else if (n.children?.length) {
                const fc = filterTree(n.children)
                if (fc.length > 0) out.push({...n, children: fc})
              }
            }
            return out
          }
          return filterTree(base)
        })()}
        columns={columns}
        size={'small'}
        pagination={{defaultPageSize: 100}}
        rowSelection={{
          selectedRowKeys: selectedEntityKeys,
          checkStrictly: true,
          onChange: (keys, selectedRows) => {
            setSelectedEntityKeys(keys as string[])
            setSelectedEntityTable(selectedRows.length > 0 ? selectedRows[0].table : null)
          },
          getCheckboxProps: (row) => ({
            disabled: branch === 'master'
              || !row.is_entity_layer
              || (row.table !== SettingTable.TABLE_BRAND && row.table !== SettingTable.TABLE_CASINO)
              || (selectedEntityTable !== null && row.table !== selectedEntityTable),
          }),
        }}
        rowClassName={(row) => row.branch === 'master' ?
          (row.virtual ? 'row-virtual' : '') :
            row.action === Action.DELETE ? 'merge-removed' :
              (row.is_env_layer || row.is_table_layer || row.is_entity_layer) ? 'merge-changed' :
                (masterRows.has(row.key) ? 'merge-changed' : 'merge-added')
        }
      />
      </div>
      </div>

      <Modal
        title={`Group edit ${selectedEntityTable !== null ? tableLabel(selectedEntityTable) + ' ' : ''}(${selectedEntityRows.length})`}
        open={groupEditOpen}
        onCancel={() => setGroupEditOpen(false)}
        onOk={() => {
          let payload: any = null
          for (const e of selectedEntityRows) {
            for (const settingPath of Object.keys(groupEditValues)) {
              const newLeaf = groupEditValues[settingPath]
              const newText = leafValueText(newLeaf)
              if (newText === '') continue
              const oldText = leafValueText(findLeaf(e, settingPath))
              if (newText === oldText) continue
              payload = upsertSettingPayload(e.env, branch, newLeaf, {
                brand: e.brand,
                casino: e.casino,
                filterType: e.filterType,
              }, payload)
            }
          }
          if (!payload) {
            setGroupEditOpen(false)
            return
          }
          providersConfig.upsertSettings(payload)
            .then(() => {
              setGroupEditOpen(false)
              setSelectedEntityRows([])
              loadSettings()
            })
            .catch((e: any) => { ErrorView(modal, 'group edit error', e) })
        }}
        width="90vw"
        footer={(_, {OkBtn, CancelBtn}) => (
          <Flex justify="space-between">
            <Button danger onClick={() => {
              modal.confirm({
                title: 'Delete all',
                content: `Delete all ${selectedEntityRows.length} selected ${selectedEntityTable !== null ? tableLabel(selectedEntityTable) : ''} entries?`,
                okType: 'danger',
                onOk: () => {
                  const done = () => {
                    setGroupEditOpen(false)
                    setSelectedEntityRows([])
                    loadSettings()
                  }

                  const flatten = (nodes: DataType[]): DataType[] =>
                    nodes.flatMap((n) => n.children?.length ? flatten(n.children) : [n])

                  const allLeaves: DataType[] = []
                  for (const e of selectedEntityRows) {
                    allLeaves.push(...flatten(e.children ?? []).filter((l) =>
                      l.setting !== ''
                      && !l.is_env_layer && !l.is_table_layer && !l.is_entity_layer
                      && !l.virtual
                    ))
                  }

                  if (allLeaves.length === 0) {
                    done()
                    return
                  }

                  const allMaster = allLeaves.every((l) => l.branch === 'master')

                  if (!allMaster) {
                    let payload: any = null
                    for (const leaf of allLeaves) {
                      payload = deleteSettingPayload(leaf.env, branch, leaf, payload)
                    }
                    providersConfig.deleteSettings(payload)
                      .then(done)
                      .catch((err: any) => { ErrorView(modal, 'delete all error', err) })
                  } else {
                    let payload: any = null
                    for (const leaf of allLeaves) {
                      payload = upsertSettingPayload(leaf.env, branch, leaf, {action: Action.DELETE}, payload)
                    }
                    providersConfig.upsertSettings(payload)
                      .then(done)
                      .catch((err: any) => { ErrorView(modal, 'delete all error', err) })
                  }
                },
              })
            }}>Delete all</Button>
            <Flex gap={8}><CancelBtn/><OkBtn/></Flex>
          </Flex>
        )}
      >
        {(() => {
          const synthesizeLeaf = (m: ModelType, val: string): DataType => ({
            key: '',
            branch: branch,
            env: env,
            provider: m.provider,
            table: m.table,
            brand: '',
            casino: '',
            filterType: FilterType.FILTER_UNKNOWN,
            setting: m.setting,
            settingType: modelSettingType(m),
            value: val,
            vaultPath: '',
            vaultKey: '',
            values: [],
            action: Action.NO_OR_UPSET,
            virtual: false,
            is_env_layer: false,
            is_table_layer: false,
            is_entity_layer: false,
          })

          return (
            <div style={{display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '8px 12px', alignItems: 'center'}}>
              {groupEditModelSettings.map((m) => {
                const uniqueByText = groupEditUniqueByText.get(m.setting) ?? new Map<string, DataType>()
                const options = [
                  {value: '', label: ''},
                  ...Array.from(uniqueByText.keys()).sort().map((v) => ({value: v, label: v})),
                ]
                const currentText = leafValueText(groupEditValues[m.setting])

                const clearOrSet = (text: string, fallback: () => DataType | null) => {
                  if (text === '') {
                    const next = {...groupEditValues}
                    delete next[m.setting]
                    setGroupEditValues(next)
                    return
                  }
                  const picked = uniqueByText.get(text) ?? fallback()
                  if (picked) setGroupEditValues({...groupEditValues, [m.setting]: picked})
                }

                return (
                  <span key={m.key} style={{display: 'contents'}}>
                    <span>{m.setting}</span>
                    {modelSettingType(m) === SettingType.SECRET ? (
                      <Select
                        value={currentText}
                        onChange={(v) => clearOrSet(v, () => null)}
                        options={options}
                        style={{width: '100%'}}
                      />
                    ) : (
                      <AutoComplete
                        value={currentText}
                        onChange={(v) => clearOrSet(v, () => synthesizeLeaf(m, v))}
                        options={options}
                        filterOption={(input, option) => (option?.value ?? '').toString().toLowerCase().includes(input.toLowerCase())}
                        style={{width: '100%'}}
                      />
                    )}
                  </span>
                )
              })}
            </div>
          )
        })()}
      </Modal>

      <Modal
        title="Edit setting"
        open={editOpen}
        onCancel={() => setEditOpen(false)}
        onOk={() => {
          if (!editRow || !editRowEdit) return

          editRowEdit.value = (editRowEdit.value ?? '').trim()
          editRowEdit.vaultPath = (editRowEdit.vaultPath ?? '').trim()
          editRowEdit.vaultKey = (editRowEdit.vaultKey ?? '').trim()
          editRowEdit.values = (editRowEdit.values ?? []).map((s) => s.trim()).filter(Boolean)

          const keyChanged =
            (editRow.table === SettingTable.TABLE_BRAND && editRowEdit.brand !== editRow.brand) ||
            (editRow.table === SettingTable.TABLE_CASINO && editRowEdit.casino !== editRow.casino) ||
            (editRow.table === SettingTable.TABLE_FILTER && multiSettingFilterTypes.has(editRow.filterType) && editRowEdit.setting !== editRow.setting)

          const arraysEqual = (a?: string[], b?: string[]) => {
            const x = a ?? [], y = b ?? []
            return x.length === y.length && x.every((v, i) => v === y[i])
          }
          const valueChanged =
            editRowEdit.value !== editRow.value ||
            editRowEdit.vaultPath !== editRow.vaultPath ||
            editRowEdit.vaultKey !== editRow.vaultKey ||
            !arraysEqual(editRowEdit.values, editRow.values)

          const doUpsert = () => {
            if (!valueChanged) {
              setEditOpen(false)
              return
            }
            providersConfig.upsertSettings(upsertSettingPayload(editRow.env, branch, editRowEdit, {}))
              .then(() => {
                setEditOpen(false)
                loadSettings()
              })
              .catch((e: any) => { ErrorView(modal, 'upsertSettings error', e) })
          }

          if (keyChanged && !editRow.virtual) {
            modal.confirm({
              title: 'Entity key changed',
              content: 'Save as a copy (keep old) or rename (delete old)?',
              okText: 'Rename',
              okType: 'danger',
              cancelText: 'Copy',
              cancelButtonProps: {type: 'primary'},
              onOk: () => {
                providersConfig.deleteSettings(deleteSettingPayload(editRow.env, branch, editRow))
                  .then(doUpsert)
                  .catch((e: any) => { ErrorView(modal, 'deleteSettings error', e) })
              },
              onCancel: () => {
                doUpsert()
              },
            })
          } else {
            doUpsert()
          }
        }}
        width="90vw"
      >
        {editRowEdit && (
          <div style={{display: 'grid', gridTemplateColumns: '120px 1fr', gap: '8px 12px', alignItems: 'center'}}>
            <span>branch</span><span>{branch}</span>
            <span>env</span><span>{editRowEdit.env}</span>
            <span>provider</span><span>{editRowEdit.provider}</span>
            <span>table</span><span>{tableLabel(editRowEdit.table)}</span>
            {editRowEdit.table === SettingTable.TABLE_BRAND && (<>
              <span>brand</span>
              <Input value={editRowEdit.brand} onChange={(e) => setEditRowEdit({...editRowEdit, brand: e.target.value})}/>
            </>)}
            {editRowEdit.table === SettingTable.TABLE_CASINO && (<>
              <span>casino</span>
              <Input value={editRowEdit.casino} onChange={(e) => setEditRowEdit({...editRowEdit, casino: e.target.value})}/>
            </>)}
            {editRowEdit.table === SettingTable.TABLE_FILTER && <><span>filter type</span><span>{filterTypeLabel(editRowEdit.filterType)}</span></>}
            <span>setting</span>
            {editRowEdit.table === SettingTable.TABLE_FILTER && multiSettingFilterTypes.has(editRowEdit.filterType) ? (
              <Input value={editRowEdit.setting} onChange={(e) => setEditRowEdit({...editRowEdit, setting: e.target.value})}/>
            ) : (
              <span>{editRowEdit.setting}</span>
            )}
            <span>type</span><span>{SettingTypeSchema.values.find((v) => v.number === editRowEdit.settingType)?.name.toLowerCase()}</span>
            {editRowEdit.table === SettingTable.TABLE_FILTER ? (<>
              <span>values</span>
              {currenciesFilterTypes.has(editRowEdit.filterType) ? (
                <CurrencySelector
                  currencies={currencies}
                  value={editRowEdit.values ?? []}
                  onChange={(v) => setEditRowEdit({...editRowEdit, values: v})}
                />
              ) : (
                <Input.TextArea
                  rows={6}
                  value={editValuesText}
                  onChange={(e) => {
                    setEditValuesText(e.target.value)
                    setEditRowEdit({...editRowEdit, values: e.target.value.split(/[\s,]+/).filter(Boolean)})
                  }}
                  placeholder="comma-separated values"
                />
              )}
            </>) : editRowEdit.settingType === SettingType.SECRET ? (<>
              <span>vault Path</span>
              <AutoComplete
                value={editRowEdit.vaultPath}
                onChange={(v) => setEditRowEdit({...editRowEdit, vaultPath: v})}
                status={vaultPathOptions(editRowEdit).some(o => o.value === editRowEdit.vaultPath || o.value + '/' === editRowEdit.vaultPath) ? undefined : 'warning'}
                options={vaultPathOptions(editRowEdit)}
              />
              <span>vault Key</span>
              <AutoComplete
                value={editRowEdit.vaultKey}
                onChange={(v) => setEditRowEdit({...editRowEdit, vaultKey: v})}
                status={editRowEdit.vaultKey === editRowEdit.setting.split('.').pop() ? undefined : 'warning'}
                options={[{value: editRowEdit.setting.split('.').pop() ?? editRowEdit.setting}]}
                style={{flex: 1}}
              />
              <span>vault Value</span>
              <Flex gap={4}>
                <Button size="small" onClick={() => {
                  providersConfig.secretCheck({
                    path: editRowEdit.vaultPath,
                    key: editRowEdit.vaultKey,
                  }).then((r) => {
                    const content = `path: ${r.pathExist ? '✓' : '✗'}   key: ${r.keyExist ? '✓' : '✗'}`
                    if (!r.pathExist || !r.keyExist) {
                      modal.warning({title: 'Secret check - No Found', content})
                    } else {
                      modal.info({title: 'Secret check - Ok', content})
                    }
                  }).catch((e: any) => { ErrorView(modal, 'secretCheck error', e) })
                }}>check</Button>
                <Button size="small" onClick={() => {
                  providersConfig.secretCheckAndCreate({
                    path: editRowEdit.vaultPath,
                    key: editRowEdit.vaultKey,
                  }).then((r) => {
                    const content = `path: ${r.pathExist ? '✓' : '✗'}   key: ${r.keyExist ? '✓' : '✗'}`
                    modal.info({title: 'Secret check and create', content})
                  }).catch((e: any) => { ErrorView(modal, 'secretCheckAndCreate error', e) })
                }}>check and create</Button>
                <Button size="small" onClick={() => {
                  providersConfig.secretRead({
                    path: editRowEdit.vaultPath,
                    key: editRowEdit.vaultKey,
                  }).then((r) => {
                    modal.info({
                      title: 'Secret read',
                      content: r.value || '(empty)',
                    })
                  }).catch((e: any) => { ErrorView(modal, 'secretRead error', e) })
                }}>read</Button>
                <Button size="small" onClick={() => {
                  setSecretWriteValue('')
                  setSecretCurrentValue('')
                  providersConfig.secretRead({path: editRowEdit.vaultPath, key: editRowEdit.vaultKey})
                    .then((r) => setSecretCurrentValue(r.value))
                    .catch(() => { /* show empty if not found */ })
                    .finally(() => setSecretWriteOpen(true))
                }}>write</Button>
              </Flex>
            </>) : (<>
              <span>value</span>
              {editRowEdit.settingType === SettingType.BOOL ? (
                <Select value={editRowEdit.value} onChange={(v) => setEditRowEdit({...editRowEdit, value: v})}
                        options={[{value: 'true', label: 'true'}, {value: 'false', label: 'false'}]}/>
              ) : editRowEdit.settingType === SettingType.INT ? (
                <Input type="number" step={1} value={editRowEdit.value} onChange={(e) => setEditRowEdit({...editRowEdit, value: e.target.value})}/>
              ) : editRowEdit.settingType === SettingType.FLOAT ? (
                <Input type="number" value={editRowEdit.value} onChange={(e) => setEditRowEdit({...editRowEdit, value: e.target.value})}/>
              ) : (
                <Input value={editRowEdit.value} onChange={(e) => setEditRowEdit({...editRowEdit, value: e.target.value})}/>
              )}
            </>)}
          </div>
        )}
      </Modal>

      <Modal
        title="New casino"
        open={newCasinoOpen}
        onCancel={() => setNewCasinoOpen(false)}
        onOk={() => {
          const target = newCasinoName.trim()
          if (!newCasinoTableRow || !target) {
            modal.warning({title: 'Casino name is empty'})
            return
          }
          const existing = new Set((newCasinoTableRow.children ?? []).map((c) => c.casino))
          if (existing.has(target)) {
            modal.warning({title: 'Casino already exists', content: `"${target}" already exists.`})
            return
          }

          let sourceLeaves: DataType[] = []
          const flatten = (nodes: DataType[]): DataType[] =>
            nodes.flatMap((n) => n.children?.length ? flatten(n.children) : [n])

          if (newCasinoFrom === '') {
            sourceLeaves = [{
              key: '',
              branch: branch,
              env: newCasinoTableRow.env,
              provider: newCasinoTableRow.provider,
              table: SettingTable.TABLE_CASINO,
              brand: '',
              casino: '',
              filterType: FilterType.FILTER_UNKNOWN,
              setting: 'brand',
              settingType: SettingType.STRING,
              value: 'default',
              vaultPath: '',
              vaultKey: '',
              values: [],
              action: Action.NO_OR_UPSET,
              virtual: false,
              is_env_layer: false,
              is_table_layer: false,
              is_entity_layer: false,
            }]
          } else {
            const source = newCasinoTableRow.children?.find((c) => c.casino === newCasinoFrom)
            sourceLeaves = flatten(source?.children ?? []).filter((l) => !l.virtual)
          }

          if (sourceLeaves.length === 0) {
            modal.warning({title: 'Nothing to copy', content: 'Source has no real settings.'})
            return
          }

          let payload: any = null
          for (const leaf of sourceLeaves) {
            payload = upsertSettingPayload(newCasinoTableRow.env, branch, leaf, {
              table: SettingTable.TABLE_CASINO,
              casino: target,
              brand: '',
              action: Action.NO_OR_UPSET,
            }, payload)
          }
          providersConfig.upsertSettings(payload)
            .then(() => {
              setNewCasinoOpen(false)
              loadSettings()
            })
            .catch((e: any) => { ErrorView(modal, 'new casino error', e) })
        }}
      >
        {newCasinoTableRow && (
          <div style={{display: 'grid', gridTemplateColumns: '120px 1fr', gap: '8px 12px', alignItems: 'center'}}>
            <span>name</span>
            <Input
              value={newCasinoName}
              onChange={(e) => setNewCasinoName(e.target.value)}
              placeholder="new casino id"
            />
            <span>copy from</span>
            <Select
              value={newCasinoFrom}
              onChange={(v) => setNewCasinoFrom(v)}
              options={[
                {value: '', label: ''},
                ...(newCasinoTableRow.children ?? []).map((c) => ({value: c.casino, label: c.casino})),
              ]}
              style={{width: '100%'}}
            />
          </div>
        )}
      </Modal>

      <Modal
        title="New brand"
        open={newBrandOpen}
        onCancel={() => setNewBrandOpen(false)}
        onOk={() => {
          const target = newBrandName.trim()
          if (!newBrandTableRow || !target) {
            modal.warning({title: 'Brand name is empty'})
            return
          }
          const existing = new Set((newBrandTableRow.children ?? []).map((c) => c.brand))
          if (existing.has(target)) {
            modal.warning({title: 'Brand already exists', content: `"${target}" already exists.`})
            return
          }

          const flatten = (nodes: DataType[]): DataType[] =>
            nodes.flatMap((n) => n.children?.length ? flatten(n.children) : [n])

          let sourceLeaves: DataType[] = []
          if (newBrandFrom === '') {
            const defaultValueFor = (t: SettingType): string => {
              switch (t) {
                case SettingType.BOOL: return 'false'
                case SettingType.INT: return '0'
                case SettingType.FLOAT: return '0'
                default: return ''
              }
            }
            const provNode = modelTree.find((p) => p.provider === newBrandTableRow.provider)
            const brandTbl = provNode?.children?.find((c) => c.table === SettingTable.TABLE_BRAND)
            const modelSettings = brandTbl?.children ?? []
            sourceLeaves = modelSettings
              .filter((m) => modelSettingType(m) !== SettingType.SECRET)
              .map((m) => ({
                key: '',
                branch: branch,
                env: newBrandTableRow.env,
                provider: newBrandTableRow.provider,
                table: SettingTable.TABLE_BRAND,
                brand: '',
                casino: '',
                filterType: FilterType.FILTER_UNKNOWN,
                setting: m.setting,
                settingType: modelSettingType(m),
                value: defaultValueFor(modelSettingType(m)),
                vaultPath: '',
                vaultKey: '',
                values: [],
                action: Action.NO_OR_UPSET,
                virtual: false,
                is_env_layer: false,
                is_table_layer: false,
                is_entity_layer: false,
              }))
          } else {
            const source = newBrandTableRow.children?.find((c) => c.brand === newBrandFrom)
            sourceLeaves = flatten(source?.children ?? []).filter((l) => !l.virtual)
          }

          if (sourceLeaves.length === 0) {
            modal.warning({title: 'Nothing to copy', content: 'Source has no real settings.'})
            return
          }

          let payload: any = null
          for (const leaf of sourceLeaves) {
            payload = upsertSettingPayload(newBrandTableRow.env, branch, leaf, {
              table: SettingTable.TABLE_BRAND,
              brand: target,
              casino: '',
              action: Action.NO_OR_UPSET,
            }, payload)
          }
          providersConfig.upsertSettings(payload)
            .then(() => {
              setNewBrandOpen(false)
              loadSettings()
            })
            .catch((e: any) => { ErrorView(modal, 'new brand error', e) })
        }}
      >
        {newBrandTableRow && (
          <div style={{display: 'grid', gridTemplateColumns: '120px 1fr', gap: '8px 12px', alignItems: 'center'}}>
            <span>name</span>
            <Input
              value={newBrandName}
              onChange={(e) => setNewBrandName(e.target.value)}
              placeholder="new brand id"
            />
            <span>copy from</span>
            <Select
              value={newBrandFrom}
              onChange={(v) => setNewBrandFrom(v)}
              options={[
                {value: '', label: ''},
                ...(newBrandTableRow.children ?? []).map((c) => ({value: c.brand, label: c.brand})),
              ]}
              style={{width: '100%'}}
            />
          </div>
        )}
      </Modal>

      <Modal
        title="New filter"
        open={newFilterOpen}
        onCancel={() => setNewFilterOpen(false)}
        onOk={() => {
          if (!newFilterTableRow || newFilterType === FilterType.FILTER_UNKNOWN) {
            modal.warning({title: 'Filter type is required'})
            return
          }
          const isMultiSetting = multiSettingFilterTypes.has(newFilterType)
          const target = isMultiSetting ? newFilterSetting.trim() : ''
          if (isMultiSetting && !target) {
            modal.warning({title: 'Setting name is required'})
            return
          }

          const sameTypeEntity = newFilterTableRow.children?.find((c) => c.filterType === newFilterType)
          if (sameTypeEntity) {
            if (!isMultiSetting) {
              modal.warning({
                title: 'Filter already exists',
                content: `"${filterTypeLabel(newFilterType)}" already exists.`,
              })
              return
            }
            if (findLeaf(sameTypeEntity, target)) {
              modal.warning({
                title: 'Filter setting already exists',
                content: `"${filterTypeLabel(newFilterType)} / ${target}" already exists.`,
              })
              return
            }
          }

          let sourceLeaves: DataType[] = []
          sourceLeaves = [{
            key: '',
            branch: branch,
            env: newFilterTableRow.env,
            provider: newFilterTableRow.provider,
            table: SettingTable.TABLE_FILTER,
            brand: '',
            casino: '',
            filterType: FilterType.FILTER_UNKNOWN,
            setting: target,
            settingType: SettingType.ARRAY,
            value: '',
            vaultPath: '',
            vaultKey: '',
            values: [],
            action: Action.NO_OR_UPSET,
            virtual: false,
            is_env_layer: false,
            is_table_layer: false,
            is_entity_layer: false,
          }]

          if (sourceLeaves.length === 0) {
            modal.warning({title: 'Nothing to create', content: 'No model settings found for this filter type.'})
            return
          }

          let payload: any = null
          for (const leaf of sourceLeaves) {
            payload = upsertSettingPayload(newFilterTableRow.env, branch, leaf, {
              table: SettingTable.TABLE_FILTER,
              filterType: newFilterType,
              brand: '',
              casino: '',
              action: Action.NO_OR_UPSET,
            }, payload)
          }
          providersConfig.upsertSettings(payload)
            .then(() => {
              setNewFilterOpen(false)
              loadSettings()
            })
            .catch((e: any) => { ErrorView(modal, 'new filter error', e) })
        }}
      >
        {newFilterTableRow && (
          <div style={{display: 'grid', gridTemplateColumns: '120px 1fr', gap: '8px 12px', alignItems: 'center'}}>
            <span>type</span>
            <Select
              value={newFilterType}
              onChange={(v) => setNewFilterType(v)}
              options={FilterTypeSchema.values
                .filter((v) => v.number !== 0)
                .map((v) => ({value: v.number, label: v.name.replace(/^FILTER_/, '').toLowerCase()}))}
              style={{width: '100%'}}
            />
            {multiSettingFilterTypes.has(newFilterType) && (<>
              <span>setting</span>
              <Input
                value={newFilterSetting}
                onChange={(e) => setNewFilterSetting(e.target.value)}
                placeholder="new setting name"
              />
            </>)}
          </div>
        )}
      </Modal>

      <ResultModal
        title={`Validation — ${branch}`}
        open={validationOpen}
        res={validationRes}
        onClose={() => setValidationOpen(false)}
      />

      <ResultModal
        title={`Export — ${branch}`}
        open={exportOpen}
        res={exportRes}
        onClose={() => setExportOpen(false)}
      />

      <Modal
        title="Import from git"
        open={importAskOpen}
        onCancel={() => setImportAskOpen(false)}
        confirmLoading={importLoading}
        cancelButtonProps={{disabled: importLoading}}
        maskClosable={!importLoading}
        closable={!importLoading}
        onOk={() => {
          const fromGitBranch = importFromGitBranch.trim() || 'master'
          setImportLoading(true);
          providersConfig.importSettings({fromGitBranch: fromGitBranch, toBranch: branch})
            .then((resp) => {
              setImportAskOpen(false);
              setImportRes(resp.res);
              setImportOpen(true)
              loadSettings()
            })
            .catch((e: any) => { ErrorView(modal, 'importSettings error', e) })
            .finally(() => setImportLoading(false))
        }}
        okText="Import"
      >
        <div style={{display: 'grid', gridTemplateColumns: '120px 1fr', gap: '8px 12px', alignItems: 'center'}}>
          <span style={{fontWeight: 500}}>from git branch</span>
          <Input
            placeholder="master"
            value={importFromGitBranch}
            onChange={(e) => setImportFromGitBranch(e.target.value)}
          />
          <span style={{fontWeight: 500}}>to branch</span>
          <span>{branch}</span>
        </div>
      </Modal>

      <ResultModal
        title={`Import from git — ${branch}`}
        open={importOpen}
        res={importRes}
        onClose={() => {
          setImportOpen(false);
          loadSettings()
        }}
      />

      <Modal
        title="New Branch from current"
        open={newBranchOpen}
        onCancel={() => setNewBranchOpen(false)}
        onOk={() => {
          const target = newBranchName.trim()
          if (!target) {
            modal.warning({title: 'Branch name is empty'})
            return
          }
          if (branches.includes(target)) {
            modal.warning({title: 'Branch already exists', content: `"${target}" already exists.`})
            return
          }
          providersConfig.copySettingsBranch({fromBranch: branch, toBranch: target})
            .then(() => {
              setNewBranchOpen(false);
              loadBranches();
              setBranch(target)
            })
            .catch((e: any) => { ErrorView(modal, 'copySettingsBranch error', e) })
        }}
      >
        <Input
          placeholder="New branch name"
          value={newBranchName}
          onChange={(e) => setNewBranchName(e.target.value)}
        />
      </Modal>

      <Modal
        title={`Tags for ${branch}`}
        open={tagsOpen}
        onCancel={() => setTagsOpen(false)}
        onOk={() => {
          providersConfig.setTagsSettings({branch: branch, tags: selectedTags})
            .then(() => { setTagsOpen(false); loadBranches() })
            .catch((e: any) => { ErrorView(modal, 'setTagsSettings error', e) })
        }}
        okText="Save"
      >
        <Checkbox.Group
          options={AVAILABLE_TAGS.map((t) => ({label: t, value: t}))}
          value={selectedTags}
          onChange={(vals) => setSelectedTags(vals as string[])}
        />
      </Modal>

      <Modal
        title="Merge branch comparison"
        open={mergeOpen}
        onCancel={() => setMergeOpen(false)}
        onOk={() => {
          modal.confirm({
            title: 'Merge branch',
            content: `Merge "${mergeParams.fromBranch}" into "${mergeParams.toBranch}"?`,
            onOk: () => {
              setMergeOpen(false);
              setMergeLoading(true);
              providersConfig.mergeSettingsDo(mergeParams)
                .then((resp) => {
                  setRows([]);
                  setBranch('master');
                  loadBranches();
                  if (resp.gitBranch || resp.gitMrLink) {
                    if (resp.gitMrLink) window.open(resp.gitMrLink, '_blank')
                    modal.info({
                      title: 'Git merge required',
                      content: (
                        <p>
                          Branch <b>{resp.gitBranch}</b> needs to be merged.{' '}
                          {resp.gitMrLink && <a href={resp.gitMrLink} target="_blank" rel="noreferrer">Open merge request</a>}
                        </p>
                      ),
                    })
                  } else {
                    modal.info({
                      title: 'Merge completed',
                      content: (
                        <p>
                          Updated <b>{resp.deltas.length}</b> setting{resp.deltas.length === 1 ? '' : 's'}.
                        </p>
                      ),
                    })
                  }
                })
                .catch((e: any) => { ErrorView(modal, 'mergeSettingsDo error', e) })
                .finally(() => setMergeLoading(false))
            },
          })
        }}
        okText="Merge"
        // Merge is only offered to users allowed to perform it on the backend.
        okButtonProps={{style: canMerge ? undefined : {display: 'none'}}}
        width="90vw"
      >
        <Table
          size="small"
          pagination={{pageSize: 25}}
          rowKey={(_, i) => String(i)}
          rowClassName={(d) => !d.valueTo ? 'merge-added' : !d.valueFrom ? 'merge-removed' : 'merge-changed'}
          dataSource={mergeDeltas}
          columns={[
            {title: 'provider', dataIndex: 'provider', key: 'provider'},
            {title: 'env', dataIndex: 'env', key: 'env'},
            {title: 'table', dataIndex: 'table', key: 'table', render: (v) => tableLabel(v)},
            {title: 'brand', dataIndex: 'brand', key: 'brand'},
            {title: 'casino', dataIndex: 'casino', key: 'casino'},
            {title: 'setting', dataIndex: 'setting', key: 'setting'},
            {
              title: 'before',
              dataIndex: 'valueTo',
              key: 'valueTo',
              render: (v) => v || <span style={{color: '#999'}}>&lt;default&gt;</span>,
            },
            {
              title: 'new',
              dataIndex: 'valueFrom',
              key: 'valueFrom',
              render: (v) => v || <span style={{color: '#999'}}>&lt;default&gt;</span>,
            },
          ]}
        />
      </Modal>
      <Modal
        title="Secret write"
        open={secretWriteOpen}
        onCancel={() => setSecretWriteOpen(false)}
        onOk={async () => {
          if (!editRowEdit) return
          try {
            await providersConfig.secretWrite({path: editRowEdit.vaultPath, key: editRowEdit.vaultKey, value: secretWriteValue})
            setSecretWriteOpen(false)
          } catch(e: any) { ErrorView(modal, 'secretWrite error', e) }
        }}
      >
        <div style={{display: 'grid', gridTemplateColumns: '80px 1fr', gap: '8px 12px', alignItems: 'center'}}>
          <span>current</span><span style={{color: secretCurrentValue ? undefined : '#999'}}>{secretCurrentValue || '(empty)'}</span>
          <span>new</span><Input value={secretWriteValue} onChange={(e) => setSecretWriteValue(e.target.value)} />
        </div>
      </Modal>

      {contextHolder}
    </>
  )
}